import React from "react";
import { Alert, Card, Spinner, Table } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGet } from "../../api";
import { EmptyState } from "../../components/ui";
import { IOpenDataReadinessTrendResponse } from "../../interfaces";

/**
 * Status colours, not a categorical series — the stack order is fixed and the
 * legend names every band, so identity never rests on colour alone. Validated
 * for colour-vision separation; the numbers repeat in the table below because
 * the amber band is under 3:1 against the card surface.
 */
const BANDS = [
  { key: "greenCount", label: "Fully ready", fill: "#198754" },
  { key: "amberCount", label: "Partial", fill: "#d39e00" },
  { key: "redCount", label: "Blocked", fill: "#dc3545" },
  { key: "errorCount", label: "Errored", fill: "#868e96" },
] as const;

export const OpenDataReadinessTrend: React.FC = () => {
  const { data, loading, error } = useGet<IOpenDataReadinessTrendResponse>(
    "open-data-readiness/trend?days=60"
  );

  const points = data?.points ?? [];

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  if (error) return <Alert variant="danger">Could not load the trend.</Alert>;
  if (points.length === 0)
    return (
      <EmptyState
        title="No history yet"
        message="The trend fills in once the sweep has run on more than one day."
      />
    );

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="fw-bold small">
          Readiness by day
          <span className="text-muted fw-normal ms-2">
            every swept merchant, stacked by its overall grade
          </span>
        </Card.Header>
        <Card.Body style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="snapshotDate" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {BANDS.map((b, i) => (
                <Bar
                  key={b.key}
                  dataKey={b.key}
                  name={b.label}
                  stackId="status"
                  fill={b.fill}
                  stroke="#fff"
                  strokeWidth={1}
                  radius={i === BANDS.length - 1 ? [3, 3, 0, 0] : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="fw-bold small">
          The same numbers
          <span className="text-muted fw-normal ms-2">
            newest first — readable without the colours
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th className="text-end">Tracked</th>
                {BANDS.map((b) => (
                  <th key={b.key} className="text-end">
                    {b.label}
                  </th>
                ))}
                <th className="text-end">% ready</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p) => (
                <tr key={p.snapshotDate}>
                  <td>{p.snapshotDate}</td>
                  <td className="text-end">{p.itemCount.toLocaleString()}</td>
                  <td className="text-end">{p.greenCount}</td>
                  <td className="text-end">{p.amberCount}</td>
                  <td className="text-end">{p.redCount}</td>
                  <td className="text-end">{p.errorCount}</td>
                  <td className="text-end fw-semibold">
                    {p.itemCount
                      ? `${Math.round((p.greenCount / p.itemCount) * 100)}%`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};
